import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle2, Home, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { useAuth } from '../context/AuthContext';
import confetti from 'canvas-confetti';
import LanguageSelector from '../components/LanguageSelector';

const SurveyComplete = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();

  useEffect(() => {
    // Trigger confetti on mount
    const duration = 2000;
    const end = Date.now() + duration;

    const colors = ['#E07A5F', '#81B29A', '#1B2A41'];

    (function frame() {
      confetti({
        particleCount: 3,
        angle: 60,
        spread: 55,
        origin: { x: 0 },
        colors: colors
      });
      confetti({
        particleCount: 3,
        angle: 120,
        spread: 55,
        origin: { x: 1 },
        colors: colors
      });

      if (Date.now() < end) {
        requestAnimationFrame(frame);
      }
    }());
  }, []);

  const handleBackToSurveys = () => {
    navigate('/survey');
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <div 
      className="min-h-screen flex flex-col items-center justify-center p-6 bg-cover bg-center"
      style={{ 
        backgroundImage: 'url(https://images.unsplash.com/photo-1761888856976-c4bc8303f65f?crop=entropy&cs=srgb&fm=jpg&q=85)'
      }}
    >
      <div className="absolute top-4 right-4 z-10">
        <LanguageSelector />
      </div>
      <Card className="max-w-md w-full shadow-2xl animate-scale-in">
        <CardContent className="p-8 md:p-12 text-center">
          {/* Success Icon */}
          <div className="flex justify-center mb-6">
            <div className="p-4 rounded-full bg-accent/10 animate-bounce">
              <CheckCircle2 className="h-16 w-16 text-accent" />
            </div>
          </div>

          {/* Title */}
          <h1 className="font-heading text-3xl font-bold text-primary mb-3">
            Thank You!
          </h1>

          {/* Description */}
          <p className="text-muted-foreground mb-8">
            Your feedback has been submitted successfully. Your voice matters and helps us create a better workplace for everyone.
          </p>

          {/* Action Buttons */}
          <div className="space-y-3">
            <Button
              onClick={handleBackToSurveys}
              data-testid="back-to-surveys-btn"
              className="w-full h-12 bg-primary hover:bg-primary/90 rounded-full"
            >
              <ArrowRight className="h-4 w-4 mr-2" />
              View More Surveys
            </Button>
            <Button
              variant="outline"
              onClick={handleLogout}
              data-testid="logout-btn"
              className="w-full h-12 rounded-full"
            >
              <Home className="h-4 w-4 mr-2" />
              Return to Home
            </Button>
          </div>

          {/* Footer Note */}
          <p className="text-xs text-muted-foreground mt-8 pt-6 border-t border-border">
            Your responses are kept confidential and will be reviewed by HR to improve workplace satisfaction.
          </p>
        </CardContent>
      </Card>
      <div className="mt-6 text-center" data-testid="global-footer">
        <p className="text-xs text-muted-foreground/70">Powered by HotelPulse.in | All Rights Reserved &copy; 2026</p>
        <p className="text-xs text-muted-foreground/50 mt-0.5">For support, connect at info@hotelpulse.in</p>
      </div>
    </div>
  );
};

export default SurveyComplete;
